
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';
import { Card, Button } from '@/components/ui';

interface PrayerResponsePayload {
  id: string;
  prayerId: string;
  responseType: string;
  textBody: string | null;
  playbackUrl: string | null;
  downloadUrl: string | null;
  videoExpiresAt: string | null;
  createdAt: string;
}

interface MyPrayerItem {
  prayer: {
    id: string;
    body: string;
    type: string;
    status: string;
    createdAt: string;
  };
  response: PrayerResponsePayload | null;
}

async function downloadFile(url: string, filename: string) {
  const res = await fetch(url);
  if (!res.ok) throw new Error('Download failed');
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(objectUrl);
}

export default function MyPrayersPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<MyPrayerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const res = await api.get<MyPrayerItem[]>('/me/my-prayers');
      if (cancelled) return;
      if (res.data) {
        setItems(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res.error || 'Failed to load your prayers');
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleDownload = useCallback(async (response: PrayerResponsePayload) => {
    if (!response.downloadUrl) return;
    setDownloadingId(response.id);
    try {
      const ext = response.responseType === 'audio' ? 'webm' : 'mp4';
      await downloadFile(response.downloadUrl, `prayer-response.${ext}`);
    } finally {
      setDownloadingId(null);
    }
  }, []);

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
        My Prayers
      </h1>
      <p className="text-slate-600 dark:text-slate-400">
        Prayers you submitted and any responses from our prayer team.
      </p>

      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
        </div>
      )}

      {error && (
        <Card padding="md" className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
          <p className="text-amber-800 dark:text-amber-200">{error}</p>
        </Card>
      )}

      {!loading && !error && items.length === 0 && (
        <Card padding="md" className="text-center text-slate-500 dark:text-slate-400">
          <p>You have not submitted any prayers yet.</p>
          <Link to="/submit" className="mt-2 inline-block text-primary-600 dark:text-primary-400 hover:underline">
            Submit a prayer request
          </Link>
        </Card>
      )}

      {!loading && items.length > 0 && (
        <ul className="space-y-4">
          {items.map(({ prayer, response }) => (
            <li key={prayer.id}>
              <Card padding="md" className="space-y-3">
                <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                  {prayer.body}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {new Date(prayer.createdAt).toLocaleDateString()} · {prayer.type === 'praise_report' ? 'Answered prayer' : 'Prayer request'}
                </p>
                {response && (
                  <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Response from our prayer team
                    </p>
                    {response.textBody && (
                      <p className="text-slate-600 dark:text-slate-400 whitespace-pre-wrap">
                        {response.textBody}
                      </p>
                    )}
                    {response.playbackUrl && response.responseType === 'video' && (
                      <video
                        controls
                        className="mt-2 max-w-full rounded-lg"
                        playsInline
                        preload="metadata"
                      >
                        <source src={response.playbackUrl} type="video/mp4" />
                      </video>
                    )}
                    {response.playbackUrl && response.responseType === 'audio' && (
                      <audio controls className="mt-2 w-full" src={response.playbackUrl} />
                    )}
                    {response.downloadUrl && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2"
                        onClick={() => handleDownload(response)}
                        disabled={downloadingId === response.id}
                      >
                        {downloadingId === response.id ? 'Downloading...' : 'Download'}
                      </Button>
                    )}
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {new Date(response.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
